import React, { useEffect, useMemo, useState } from "react";
import {
  Area, AreaChart, Bar, Brush, CartesianGrid, ComposedChart, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Loader2, RefreshCw, Cloud, Zap, Droplet, X, Maximize2, Minimize2, BarChart3, LineChart as LineIcon } from "lucide-react";
import { fetchKCESeries } from "@/lib/api";
import { useChartZoom } from "@/hooks/useChartZoom";

const COLORS = {
  price: "#FFFFFF",
  canopyBull: "#A98CFF",
  canopyBear: "#FF3B30",
  emaFast: "#FFD60A",
  emaSlow: "#A1A1AA",
  rsi: "#007AFF",
  entry: "#FFFFFF",
  tp: "#FFD60A",
  sl: "#FF3B30",
  long: "#00C805",
  short: "#FF3B30",
};

const RANGES = [
  { id: "30", days: 30, label: "1M" },
  { id: "60", days: 60, label: "3M" },
  { id: "120", days: 120, label: "6M" },
];

const fmtTick = (t) => {
  if (!t) return "";
  const parts = t.split("-");
  if (parts.length !== 3) return t;
  return `${parts[1]}/${parts[2]}`;
};

const KCEChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const Item = ({ name, value, color }) => (
    value === null || value === undefined ? null : (
      <div className="flex items-center justify-between gap-3 text-[11px]">
        <span className="inline-flex items-center gap-1.5 text-white/55">
          <span className="w-1.5 h-1.5 rounded-sm" style={{ background: color }} /> {name}
        </span>
        <span className="num text-white/90">{typeof value === "number" ? value.toFixed(2) : value}</span>
      </div>
    )
  );
  const COLORS_MAP = {
    res: "#FF9500",
    sup: "#34C759",
  };
  return (
    <div className="bg-[#0c0c0e]/95 border border-white/15 rounded-md p-2.5 backdrop-blur-md shadow-xl min-w-[180px]">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1.5">{label}</div>
      <div className="space-y-0.5">
        <Item name="Open" value={row.open} color="#A1A1AA" />
        <Item name="High" value={row.high} color="#00C805" />
        <Item name="Low"  value={row.low}  color="#FF3B30" />
        <Item name="Close" value={row.close} color={COLORS.price} />
        <Item name="EMA fast" value={row.ema_fast} color={COLORS.emaFast} />
        <Item name="EMA slow" value={row.ema_slow} color={COLORS.emaSlow} />
        <Item name="Canopy top" value={row.canopy_top} color={COLORS.canopyBull} />
        <Item name="Canopy bot" value={row.canopy_bottom} color={COLORS.canopyBull} />
        <Item name="Resistance" value={row.resistance_level} color={COLORS_MAP.res} />
        <Item name="Support" value={row.support_level} color={COLORS_MAP.sup} />
        <Item name="RSI" value={row.rsi} color={COLORS.rsi} />
      </div>
    </div>
  );
};

const LayerChip = ({ Icon, label, value, color }) => (
  <div
    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded border"
    style={{ borderColor: `${color}40`, background: `${color}10`, color }}
  >
    <Icon className="w-2.5 h-2.5" /> {label} · <span className="opacity-80 normal-case tracking-normal">{value}</span>
  </div>
);

// Custom candlestick shape — uses Recharts' Bar bounding box (which maps to [low, high])
// and interpolates open/close within that range to draw the body.
const Candle = (props) => {
  const { x, y, width, height, payload } = props;
  if (!payload || payload.open == null || payload.close == null || payload.high == null || payload.low == null) {
    return null;
  }
  const { open: o, high: h, low: l, close: c } = payload;
  const range = h - l;
  if (range <= 0) return null;
  const cx = x + width / 2;
  const wickW = Math.max(0.6, Math.min(1.4, width * 0.08));
  const bodyW = Math.max(1.2, width * 0.62);
  const bodyX = cx - bodyW / 2;
  const openY = y + ((h - o) / range) * height;
  const closeY = y + ((h - c) / range) * height;
  const bodyTop = Math.min(openY, closeY);
  const bodyH = Math.max(0.8, Math.abs(closeY - openY));
  const up = c >= o;
  const fill = up ? "#00C805" : "#FF3B30";
  return (
    <g>
      <line
        x1={cx} x2={cx}
        y1={y} y2={y + height}
        stroke={fill}
        strokeWidth={wickW}
      />
      <rect
        x={bodyX}
        y={bodyTop}
        width={bodyW}
        height={bodyH}
        fill={up ? fill : fill}
        opacity={up ? 0.95 : 0.95}
      />
    </g>
  );
};

const KCESignalChart = ({ symbol, onClose, embedded = false, settings = null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeId, setRangeId] = useState("60");
  const [expanded, setExpanded] = useState(false);
  const [chartMode, setChartMode] = useState(() => {
    try {
      const saved = localStorage.getItem("kce_chart_mode");
      return saved === "candles" ? "candles" : "line";
    } catch {
      return "line";
    }
  }); // "line" | "candles"

  useEffect(() => {
    try { localStorage.setItem("kce_chart_mode", chartMode); } catch { /* ignore */ }
  }, [chartMode]);

  const range = RANGES.find((r) => r.id === rangeId) || RANGES[1];

  // Inline the load so we can attach a per-fire cancel token. Effect deps
  // use a stable serialized settings string so identity-different objects
  // with identical contents don't trigger redundant fetches.
  const settingsKey = JSON.stringify(settings || {});
  useEffect(() => {
    if (!symbol) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchKCESeries(symbol, 120, settings)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) console.error("kce series failed", e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, settingsKey]);

  const load = () => {
    // Manual refresh button — same cancel-safe pattern.
    if (!symbol) return;
    setLoading(true);
    fetchKCESeries(symbol, 120, settings)
      .then((d) => setData(d))
      .catch((e) => console.error("kce series failed", e))
      .finally(() => setLoading(false));
  };

  const bars = useMemo(() => {
    if (!data?.bars) return [];
    const n = Math.min(range.days, data.bars.length);
    const slice = data.bars.slice(-n);
    // Pre-compute [low, high] range for the candle Bar dataKey
    return slice.map((b) => ({
      ...b,
      _range: b.low != null && b.high != null ? [b.low, b.high] : null,
    }));
  }, [data, range]);

  const lastBar = bars.length ? bars[bars.length - 1] : null;
  const lastRsi = lastBar?.rsi ?? null;
  const thresholds = data?.thresholds || { vapor_overbought: 70, vapor_oversold: 30 };

  const yDomain = useMemo(() => {
    if (!bars.length) return ["auto", "auto"];
    const vals = bars.flatMap((b) => [
      b.close, b.canopy_top, b.canopy_bottom, b.ema_fast, b.ema_slow,
      b.resistance_level, b.support_level,
      b.high, b.low,
    ]).filter((v) => v != null);
    if (!vals.length) return ["auto", "auto"];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = (max - min) * 0.07;
    return [Math.max(0, min - pad), max + pad];
  }, [bars]);

  const isLong = data?.signal === "long";
  const isShort = data?.signal === "short";
  const signalColor = isLong ? COLORS.long : isShort ? COLORS.short : "#A1A1AA";

  // Zoom: index-based slicing, syncs across the price pane and the RSI sub-pane.
  const zoom = useChartZoom({
    data: bars,
    mode: "index",
    resetDeps: [symbol, rangeId],
  });
  const viewBars = zoom.slicedData;

  const containerClass = embedded
    ? "bg-[#0a0a0c] border border-white/10 rounded-lg p-3"
    : expanded
      ? "fixed inset-2 sm:inset-6 z-50 bg-[#0a0a0c] border border-white/15 rounded-xl p-4 sm:p-6 overflow-auto shadow-2xl"
      : "bg-[#0a0a0c] border border-white/10 rounded-lg p-4";

  const chartHeight = embedded ? 240 : expanded ? Math.min(window.innerHeight - 360, 600) : 320;
  const rsiHeight = embedded ? 60 : expanded ? 100 : 80;

  return (
    <div className={containerClass} data-testid={`kce-chart-${symbol}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-white truncate">{symbol}</span>
          {data?.signal && (
            <span
              className="text-[9px] uppercase tracking-[0.18em] font-medium px-1.5 py-0.5 rounded border"
              style={{ color: signalColor, borderColor: `${signalColor}55`, background: `${signalColor}14` }}
            >
              {data.signal}
            </span>
          )}
          {data?.layers && (
            <div className="hidden md:flex items-center gap-1 ml-1">
              <LayerChip Icon={Cloud} label="Canopy" value={(data.layers.canopy || "—").replace("_", " ")} color={COLORS.canopyBull} />
              <LayerChip Icon={Zap} label="Vector" value={(data.layers.launch_vector || "—").replace("_", " ")} color={COLORS.emaFast} />
              <LayerChip Icon={Droplet} label="Vapor" value={(data.layers.vapor || "—").replace("_", " ")} color={COLORS.rsi} />
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {/* Chart-type toggle (line vs candles) */}
          <div className="inline-flex items-center gap-0.5 bg-[#050505] border border-white/10 rounded-md p-0.5">
            <button
              type="button"
              onClick={() => setChartMode("line")}
              data-testid={`kce-chart-mode-line-${symbol}`}
              title="Line chart"
              className={
                "inline-flex items-center justify-center w-6 h-6 rounded transition " +
                (chartMode === "line" ? "bg-white text-black" : "text-white/55 hover:text-white")
              }
            >
              <LineIcon className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => setChartMode("candles")}
              data-testid={`kce-chart-mode-candles-${symbol}`}
              title="Candlestick chart"
              className={
                "inline-flex items-center justify-center w-6 h-6 rounded transition " +
                (chartMode === "candles" ? "bg-white text-black" : "text-white/55 hover:text-white")
              }
            >
              <BarChart3 className="w-3 h-3" />
            </button>
          </div>
          <div className="inline-flex items-center gap-0.5 bg-[#050505] border border-white/10 rounded-md p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRangeId(r.id)}
                data-testid={`kce-range-${r.id}`}
                className={
                  "text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded transition " +
                  (rangeId === r.id ? "bg-white text-black" : "text-white/55 hover:text-white")
                }
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            data-testid={`kce-chart-refresh-${symbol}`}
            className="inline-flex items-center justify-center w-6 h-6 rounded border border-white/10 text-white/55 hover:text-white hover:border-white/25 transition"
            title="Refresh"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
          {!embedded && (
            <div className="hidden sm:flex">
              <zoom.Controls compact />
            </div>
          )}
          {!embedded && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`kce-chart-expand-${symbol}`}
              className="inline-flex items-center justify-center w-6 h-6 rounded border border-white/10 text-white/55 hover:text-white hover:border-white/25 transition"
              title={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              data-testid={`kce-chart-close-${symbol}`}
              className="inline-flex items-center justify-center w-6 h-6 rounded border border-white/10 text-white/55 hover:text-white hover:border-white/25 transition"
              title="Close"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Loader / empty / chart */}
      {loading && !data ? (
        <div className="flex items-center justify-center text-white/55 text-xs gap-1.5" style={{ height: chartHeight }}>
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : !bars.length ? (
        <div className="flex items-center justify-center text-white/45 text-xs" style={{ height: chartHeight }}>
          No data available
        </div>
      ) : (
        <div ref={zoom.wheelHostRef}>
          {/* Price + Canopy chart */}
          <ResponsiveContainer width="100%" height={chartHeight}>
            <ComposedChart data={viewBars} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id={`canopyFill-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={isShort ? COLORS.canopyBear : COLORS.canopyBull} stopOpacity="0.18" />
                  <stop offset="100%" stopColor={isShort ? COLORS.canopyBear : COLORS.canopyBull} stopOpacity="0.04" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="t" tickFormatter={fmtTick} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
              <YAxis domain={yDomain} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" width={48} />
              <Tooltip content={<KCEChartTooltip />} />

              {/* Canopy band — area between top/bottom (fill via canopy_top, baseline canopy_bottom) */}
              <Area
                type="monotone"
                dataKey="canopy_top"
                stroke={isShort ? COLORS.canopyBear : COLORS.canopyBull}
                strokeOpacity={0.6}
                strokeWidth={1}
                fill={`url(#canopyFill-${symbol})`}
                isAnimationActive={false}
                dot={false}
                connectNulls
                baseLine={(d) => d?.canopy_bottom}
              />
              <Line type="monotone" dataKey="canopy_bottom" stroke={isShort ? COLORS.canopyBear : COLORS.canopyBull} strokeOpacity={0.4} strokeWidth={1} dot={false} isAnimationActive={false} connectNulls />

              {/* EMA fast / slow */}
              <Line type="monotone" dataKey="ema_fast" stroke={COLORS.emaFast} strokeWidth={1.2} dot={false} isAnimationActive={false} connectNulls />
              <Line type="monotone" dataKey="ema_slow" stroke={COLORS.emaSlow} strokeWidth={1.2} dot={false} strokeDasharray="4 3" isAnimationActive={false} connectNulls />

              {/* Price — line or candles */}
              {chartMode === "candles" ? (
                <Bar
                  dataKey="_range"
                  shape={<Candle />}
                  isAnimationActive={false}
                  legendType="none"
                />
              ) : (
                <Line type="monotone" dataKey="close" stroke={COLORS.price} strokeWidth={1.6} dot={false} isAnimationActive={false} />
              )}

              {/* Trade levels (when active signal) */}
              {data?.entry != null && (
                <ReferenceLine y={data.entry} stroke={COLORS.entry} strokeWidth={1.2} strokeDasharray="2 4"
                  label={{ value: `Entry $${data.entry}`, fill: COLORS.entry, position: "right", fontSize: 10 }} />
              )}
              {data?.tp != null && (
                <ReferenceLine y={data.tp} stroke={COLORS.tp} strokeWidth={1.2} strokeDasharray="2 4"
                  label={{ value: `TP $${data.tp}`, fill: COLORS.tp, position: "right", fontSize: 10 }} />
              )}
              {data?.sl != null && (
                <ReferenceLine y={data.sl} stroke={COLORS.sl} strokeWidth={1.2} strokeDasharray="2 4"
                  label={{ value: `SL $${data.sl}`, fill: COLORS.sl, position: "right", fontSize: 10 }} />
              )}

              {/* Pillar 4 — Structural Support / Resistance (step lines) */}
              <Line type="stepAfter" dataKey="resistance_level" stroke="#FF9500" strokeWidth={1} strokeOpacity={0.7}
                strokeDasharray="1 3" dot={false} isAnimationActive={false} connectNulls />
              <Line type="stepAfter" dataKey="support_level" stroke="#34C759" strokeWidth={1} strokeOpacity={0.7}
                strokeDasharray="1 3" dot={false} isAnimationActive={false} connectNulls />

              {/* Zoom brush */}
              <Brush
                dataKey="t"
                height={18}
                stroke="rgba(255,255,255,0.25)"
                fill="rgba(255,255,255,0.04)"
                travellerWidth={6}
                tickFormatter={fmtTick}
                data-testid={`kce-chart-brush-${symbol}`}
              />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Vapor / RSI sub-chart */}
          <div className="mt-1">
            <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.18em] text-white/45 mb-0.5 px-1">
              <span className="inline-flex items-center gap-1">
                <Droplet className="w-2.5 h-2.5 text-[#007AFF]" /> Vapor Index (RSI)
              </span>
              <span className="num text-white/75">{lastRsi == null ? "—" : lastRsi.toFixed(1)}</span>
            </div>
            <ResponsiveContainer width="100%" height={rsiHeight}>
              <AreaChart data={viewBars} margin={{ top: 2, right: 8, left: 0, bottom: 2 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="t" hide />
                <YAxis domain={[0, 100]} ticks={[30, 50, 70]} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 9 }} stroke="rgba(255,255,255,0.1)" width={28} />
                <ReferenceLine y={thresholds.vapor_overbought} stroke={COLORS.tp} strokeDasharray="2 4" />
                <ReferenceLine y={thresholds.vapor_oversold} stroke={COLORS.tp} strokeDasharray="2 4" />
                <ReferenceLine y={50} stroke="rgba(255,255,255,0.15)" />
                <Area type="monotone" dataKey="rsi" stroke={COLORS.rsi} strokeWidth={1.2} fill={COLORS.rsi} fillOpacity={0.10} dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Legend strip */}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-white/55 px-1">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-0.5" style={{ background: COLORS.price }} /> Price</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-0.5" style={{ background: COLORS.emaFast }} /> EMA fast</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-0.5 border-t border-dashed" style={{ borderColor: COLORS.emaSlow }} /> EMA slow</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: `${isShort ? COLORS.canopyBear : COLORS.canopyBull}33`, border: `1px solid ${isShort ? COLORS.canopyBear : COLORS.canopyBull}` }} /> Canopy</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-0.5 border-t border-dashed" style={{ borderColor: "#FF9500" }} /> Resistance</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-0.5 border-t border-dashed" style={{ borderColor: "#34C759" }} /> Support</span>
            {data?.entry != null && (
              <span className="inline-flex items-center gap-1"><span className="w-2 h-0.5 border-t border-dashed" style={{ borderColor: COLORS.tp }} /> Entry / TP / SL</span>
            )}
            <span className="ml-auto text-white/35">
              Drag the bottom strip to zoom · {bars.length} bars
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default KCESignalChart;
